<template>
  <div class="">
    <div class="panel-topnav container-fluid">
      <ul class="nav nav-tabs pull-left">
        <li>
          <h3 class="page-title">
            Investors
          </h3>
        </li>
      </ul>
      <ul class="nav nav-tabs ml-auto">
        <li class="help-container">
          <i
            class="ion-help-circled show-onboarding-tour"
            @click="openHelpDialog()"
          ></i>
        </li>
      </ul>
    </div>

    <Filters
      class="container-fluid filters"
      @change="onFiltersChange"
      @add-button-clicked="$refs.createInvestorModal.open()"
    />

    <div class="container-fluid pane">
      <CreateInvestorModal
        ref="createInvestorModal"
        @investor-created="onInvestorCreated"
      />

      <InvestorListModal ref="investorListModal" />

      <div class="row">
        <div class="col-sm-12" :class="{ 'loader-cover': fetchInProgress }">
          <div
            v-show="investors.length === 0 && !fetchInProgress"
            class="alert alert-info"
          >
            <p>There are no investors to show</p>
          </div>

          <PageNumberLinks
            v-if="investors.length"
            :page="page"
            :total="totalCount"
            show-total
          />

          <table v-show="investors.length > 0" class="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Location</th>
                <th v-if="user.isStartup">
                  Status
                </th>
                <th>Investment</th>
                <th v-if="user.isAdmin" class="center">
                  Public
                </th>
                <th v-if="user.isAdmin">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="investor in investors" :key="investor.id">
                <td>
                  <a
                    href
                    @click.prevent="$refs.investorListModal.open(investor)"
                    >{{ investor.name }}</a
                  >
                </td>
                <td>
                  <span v-if="investor.country">
                    {{ investor.country | countryName }}
                  </span>
                  <span v-if="investor.country && investor.city">,</span>
                  <span v-if="investor.city">{{ investor.city }}</span>
                </td>
                <td v-if="user.isStartup">
                  {{ investor.state || '-' }}
                </td>
                <td>
                  <InlineRange
                    :min="investor.investment_min"
                    :max="investor.investment_max"
                  />
                </td>
                <td v-if="user.isAdmin">
                  <el-tooltip
                    :content="investor.is_public ? 'Unpublish' : 'Publish'"
                    placement="top"
                  >
                    <button
                      class="btn btn-xs"
                      :class="{
                        'btn-success': investor.is_public,
                        'btn-danger': !investor.is_public,
                      }"
                      @click.prevent="togglePublic(investor)"
                    >
                      <i
                        v-if="investor.is_public"
                        class="ion ion-checkmark"
                      ></i>
                      <i v-if="!investor.is_public" class="ion ion-close"></i>
                    </button>
                  </el-tooltip>
                </td>
                <td v-if="user.isAdmin">
                  <el-tooltip content="Edit" placement="top">
                    <router-link
                      :to="{
                        name: 'adminInvestorEditor',
                        params: { id: investor.id },
                      }"
                      class="btn btn-primary btn-xs"
                    >
                      <i class="ion ion-edit"></i>
                    </router-link>
                  </el-tooltip>
                  <el-tooltip content="Apply to" placement="top">
                    <a
                      href
                      class="btn btn-primary btn-xs"
                      @click.prevent="applyTo(investor)"
                    >
                      <i class="ion ion-magnet"></i>
                    </a>
                  </el-tooltip>
                  <el-tooltip content="Delete" placement="top">
                    <a
                      href
                      class="btn btn-danger btn-xs"
                      @click.prevent="deleteInvestor(investor)"
                    >
                      <i class="ion ion-trash-b"></i>
                    </a>
                  </el-tooltip>
                </td>
              </tr>
            </tbody>
          </table>

          <PageNumberLinks
            v-if="investors.length"
            :page="page"
            :total="totalCount"
            show-total
          />

          <ConfirmModal
            ref="confirmInvestorDeletionModal"
            confirm-text="Remove"
            @confirm="confirmInvestorDeletion"
          >
            <h4 slot="modal-header" class="modal-title text-danger">
              Remove investor
            </h4>
            <div slot="modal-body">
              <p>
                Are you sure you want to remove investor "{{
                  pendingDeletionInvestor.name
                }}"?
              </p>
            </div>
          </ConfirmModal>

          <hr />

          <div v-show="!user.isStartup" class="pull-right">
            <!-- temporary disable downloading contacts -->
            <button
              v-if="user.isAdmin"
              class="btn btn-success hidden"
              :disabled="investors.length === 0"
              @click.prevent="downloadMembersExcel()"
            >
              <i class="ion ion-android-download"></i>
              Download all contacts
            </button>
            <button
              class="btn btn-primary hidden"
              @click.prevent="downloadExcel()"
            >
              <i class="ion ion-android-download"></i>
              Download as Excel
            </button>
          </div>

          <br /><br /><br /><br /><br />
        </div>
      </div>
    </div>

    <ApplyAsStartupDialog
      ref="applyAsStartupDialog"
      @applied="onApplied($event)"
    />
  </div>
</template>

<script>
import _ from 'lodash'
import { mapState } from 'vuex'
import http from 'services/http'
import router from 'routes'
import { currency, roundedNumber } from '@/filters'
import InlineRange from '@/components/InlineRange'
import notifications from '@/services/notifications'
import PageNumberLinks from '@/components/PageNumberLinks'
import CreateInvestorModal from './CreateInvestorModal'
import investorListTour from './investorListTour'
import InvestorListModal from './InvestorListModal'
import { countryName } from '@/services/countries'
import Filters from './Filters.vue'
import { omitEmptyKeys } from 'utils'
import ApplyAsStartupDialog from './ApplyAsStartupDialog.vue'

export default {
  filters: { currency, countryName, roundedNumber },
  components: {
    ApplyAsStartupDialog,
    CreateInvestorModal,
    InvestorListModal,
    InlineRange,
    PageNumberLinks,
    Filters,
  },
  data: () => ({
    lastClickedPasscodeButtonInvestorId: null,
    lastClickedLinkButtonInvestorId: null,
    pendingDeletionInvestor: {},
    onboardingInProgress: false,
    startupId: null,
  }),
  computed: {
    ...mapState({
      fetchInProgress: (state) => state.investorList.fetchInProgress,
      page: (state) => state.investorList.page,
      totalCount: (state) => state.investorList.totalCount,
      investors: (state) => state.investorList.investors,
      user: (state) => state.user.data,
    }),
  },
  watch: {
    '$route.query.page'() {
      this.fetch()
    },
  },
  async created() {
    const user = await this.$store.dispatch('user/getUser')
    if (user.isStartup) {
      this.startupId = user.startup.id
    }
    this.fetch()
  },
  methods: {
    onFiltersChange(filters) {
      this.filters = filters
      this.performFiltering()
    },
    fetch(extraFilters) {
      extraFilters = extraFilters || {}
      const filters = { ...this.getFilters(), ...extraFilters }
      // TODO: include this.startupId if needed
      if (this.startupId) {
        filters.startupId = this.startupId
      }
      this.$store.dispatch('investorList/fetch', filters)
    },
    downloadCsv() {
      const request = http.getAuthed('/investors.csv', {
        returnRawRequest: true,
      })
      request
        .then(function (res) {
          const result = document.createElement('a')
          result.download = 'vestbee-investors.csv'
          const blob = new Blob([res.data])
          result.href = window.URL.createObjectURL(blob)
          result.click()
        })
        .catch((res) => {
          console.log(' error', res)
        })
    },
    downloadExcel() {
      const request = http.getAuthed('/investors.xls', {
        returnRawRequest: true,
      })
      request
        .then(function (res) {
          const result = document.createElement('a')
          result.download = 'vestbee-investors.xls'

          return res.blob().then((data) => {
            result.href = window.URL.createObjectURL(data)
            result.click()
          })
          return result
        })
        .catch((res) => {
          console.log(' error', res)
        })
    },
    async downloadMembersExcel() {
      const url = '/investors/all-members.xlsx'
      const filters = this.getFilters()
      try {
        await http.downloadFile(url, 'investor-contacts.xlsx', filters)
      } catch (error) {
        this.$notify({
          type: 'error',
          title: '',
          message: 'Something went wrong',
        })
        throw error
      }
    },
    getCsvUrl() {
      return http.getApiUrl('/investors.csv')
    },
    onInvestorCreated({ investor }) {
      this.$refs.createInvestorModal.close()
      this.$store.commit('investorList/ADD_INVESTOR', investor)
      notifications.notify({
        title: `Added investor ${investor.name}`,
        type: 'success',
      })
    },
    applyTo(investor) {
      this.$refs.applyAsStartupDialog.open(investor)
    },
    onApplied({ investorId, startupId }) {
      this.$notify.success({ title: 'Successfully applied' })
    },
    deleteInvestor(investor) {
      this.pendingDeletionInvestor = investor
      this.$refs.confirmInvestorDeletionModal.open()
    },
    confirmInvestorDeletion() {
      this.$store.dispatch(
        'investorList/remove',
        this.pendingDeletionInvestor.id,
      )
      notifications.notify({
        title: `Deleted investor ${this.pendingDeletionInvestor.name}`,
        type: 'success',
      })
      this.pendingDeletionInvestor = {}
      this.$refs.confirmInvestorDeletionModal.close()
    },

    performFiltering: _.debounce(function () {
      // perform search if at least two characters were typed
      // also perform for zero characters (user might want to reset query)
      if (this.filters.search.length !== 1) {
        this.fetch({ page: 1 })
      }
    }, 300),

    getFilters() {
      const page = parseInt(this.$route.query.page, 10)
      return omitEmptyKeys({ page, ...this.filters })
    },
    openHelpDialog() {
      investorListTour.start()
    },
    togglePublic(investor) {
      if (investor.is_public) {
        this.unpublishInvestor(investor)
      } else {
        this.publishInvestor(investor)
      }
    },
    async publishInvestor(investor) {
      await this.$store.dispatch('investorList/publish', { investor })
      notifications.notify({ title: 'Published investor', type: 'success' })
    },
    async unpublishInvestor(investor) {
      await this.$store.dispatch('investorList/unpublish', { investor })
      notifications.notify({ title: 'Unpublished investor', type: 'success' })
    },
  },
}
</script>

<style lang="scss" scoped>
.table {
  margin: 10px;
}
.card {
  label {
    margin: 5px 5px 0px 0px;
    font-weight: normal;
    font-size: 12px;
  }
  .btn {
    margin: 0px 0px 5px 0px;
    text-align: center;
    width: 80%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.help-container {
  margin: 20px 12px;
}
</style>
